import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { getInviteInfo, acceptInvite } from '@/lib/auth';
import { useT } from '@/lib/LanguageContext';

export default function AcceptInvite() {
  const t = useT();
  const { token } = useParams();
  const navigate = useNavigate();

  const [invite, setInvite] = useState(null);
  const [inviteError, setInviteError] = useState(null);
  const [loadingInvite, setLoadingInvite] = useState(true);

  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const info = await getInviteInfo(token);
        setInvite(info);
      } catch (err) {
        setInviteError(err.message || 'This invite link is invalid or has expired.');
      } finally {
        setLoadingInvite(false);
      }
    };
    load();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const existing = invite?.existing_account;
    if (!existing && password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (!existing && password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setSubmitting(true);
    try {
      await acceptInvite(token, { full_name: fullName, password });
      toast.success(existing ? `${invite.company_name} has been added to your companies.` : 'Account created! Welcome to Stagehead OS.');
      window.location.href = '/dashboard';
    } catch (err) {
      toast.error(existing ? 'Could not join' : 'Failed to create account', { description: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ backgroundColor: 'hsl(var(--sidebar))' }}
    >
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <img src="/stagehead-os-mark.svg" alt="Stagehead OS" className="w-16 h-16 rounded-2xl" />
          </div>
          <div>
            <CardTitle className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t('auth.accept_invite')}
            </CardTitle>
            {invite && (
              <CardDescription className="mt-1">
                Joining <strong>{invite.company_name}</strong> as {invite.role}
              </CardDescription>
            )}
          </div>
        </CardHeader>

        <CardContent>
          {loadingInvite && (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {!loadingInvite && inviteError && (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <XCircle className="w-10 h-10 text-red-500" />
              <p className="font-medium text-[#1E2A33]">Invite unavailable</p>
              <p className="text-sm text-muted-foreground">{inviteError}</p>
              <Button variant="outline" onClick={() => navigate('/login')} className="mt-2">
                Back to Sign In
              </Button>
            </div>
          )}

          {!loadingInvite && invite && invite.existing_account && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-sm text-[#1E2A33]">
                You already have a Stagehead login{invite.name ? `, ${invite.name.split(' ')[0]}` : ''}. Sign in with it to add <strong>{invite.company_name}</strong> to your companies. You'll move between them from the sidebar, with one login.
              </p>
              <div className="space-y-2">
                <Label>{t('auth.email')}</Label>
                <Input value={invite.email} disabled className="bg-muted" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Your current password</Label>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />
              </div>
              <Button type="submit" disabled={submitting} className="w-full" size="lg" style={{ backgroundColor: 'hsl(var(--accent))' }}>
                {submitting ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />{t('common.loading')}</> : `Join ${invite.company_name}`}
              </Button>
              <p className="text-xs text-center text-muted-foreground">Forgot it? <a href="/login" className="underline">Reset it from the sign-in page</a>, then come back to this link.</p>
            </form>
          )}

          {!loadingInvite && invite && !invite.existing_account && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>{t('auth.email')}</Label>
                <Input value={invite.email} disabled className="bg-muted" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="full-name">{t('auth.full_name')}</Label>
                <Input
                  id="full-name"
                  placeholder="Jane Smith"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  autoComplete="name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{t('auth.password')}</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Min 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  placeholder="Repeat password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full"
                size="lg"
                style={{ backgroundColor: 'hsl(var(--accent))' }}
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    {t('common.loading')}
                  </>
                ) : (
                  t('auth.create_account')
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
